import styles from './testPlanMenu.module.scss';
import { useTranslation } from "react-i18next";
import { ReactComponent as AddIcon } from "../../shared/images/add.svg";
import { Accordion } from "../../shared/components/accordion/accordion";
import { useGetAllScenariosQuery } from "../../api/scenarioService";
import { useGetAllTestCaseTemplateQuery } from "../../api/testCaseTemplateService";
import arrowIcon from '../../shared/images/arrow.svg';
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import { TransformExecuteResponse } from "../../types/clientTypes";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import infoIcon from "../../shared/images/info.svg";
import editIcon from "../../shared/images/edit.svg";
import { deleteSelectedTestCaseTemplate } from "../../store/slices/testCaseTemplateSlice";
import { TestPlanScenarios } from "./testPlanScenarios/testPlanScenarios";
import { useGetAllTestPlanQuery } from "../../api/testPlanService";
import {
  deleteTestPlan,
  deleteTestPlanExecuteResponse,
  deleteTestPlanExecuteResponseByIds,
  setEditTestCaseId,
  setTestPlan,
  setTestPlanExecuteResponse,
  updateTestPlanExecuteResponse,
  updateTestPlanSelectedScenarios
} from "../../store/slices/testPlanSlice";
import {TestPlan, TestPlanScenario} from "../../types/testPlanTypes";
import { TestPlanModal } from "../testPlanModal/testPlanModal";
import { SearchInput } from "../../shared/components/searchInput/searchInput";
import { setShowSpinner } from "../../store/slices/mainSlice";
import { TransformTestCase } from "../../types/testCaseTemplateTypes";
import { RunTestCasesButton } from "../runTestCasesButton/runTestCasesButton";
import { getTransformTestCase } from "../../utils/testCaseUtils";

export const TestPlanMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>()

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const selectedTestPlan = useSelector((state: RootState) => state.testPlanSlice.selectedTestPlan);
  const editTestCaseId = useSelector((state: RootState) => state.testPlanSlice.editTestCaseId);
  const selectedScenarios = useSelector((state: RootState) => state.testPlanSlice.selectedScenarios);

  const [activeIndex, setActiveIndex] = useState<number>();
  const [showTestPlanModal, setShowTestPlanModal] = useState<boolean>(false)
  const [editTestPlan, setEditTestPlan] = useState<TestPlan>()
  const [searchValue, setSearchValue] = useState<string>("")
  const [filteredTestPlans, setFilteredTestPlans] = useState<Array<TestPlan>>([]);

  const { data: testPlans, refetch: refetchTestPlans, isLoading: isLoadingTestPlans, isFetching: isFetchingTestPlans } = useGetAllTestPlanQuery("", { refetchOnMountOrArgChange: true });
  const { data: testCaseTemplates, isLoading: isLoadingTemplates, isFetching: isFetchingTemplates } = useGetAllTestCaseTemplateQuery("", { refetchOnMountOrArgChange: true });
  const { data: scenarios, isLoading: isLoadingScenarios } = useGetAllScenariosQuery("");

  const transformTestCases = useCallback((editTestCaseId?: number): void => {
    const newSelectedScenario: Array<TestPlanScenario> = [];
    selectedScenarios.forEach(scenario => {
      const newTestCases: Array<TransformTestCase> = [];
      scenario.testCases.forEach(testCase => {
        if (editTestCaseId != null && testCase.id !== editTestCaseId) {
          newTestCases.push(testCase);
          return;
        }

        const template = testCaseTemplates?.find(item => item.id === testCase.testCaseTemplateId);
        if (!template) {
          console.error("Error find Template");
          return;
        }

        const newTestCase = getTransformTestCase(selectedVariables, template, testCase);
        newTestCases.push(newTestCase);
      });
      newSelectedScenario.push({
        ...scenario,
        testCases: newTestCases
      });
    });
    dispatch(updateTestPlanSelectedScenarios(newSelectedScenario));
  }, [dispatch, selectedScenarios, testCaseTemplates, selectedVariables]);

  useEffect(() => {
    transformTestCases();
    dispatch(deleteTestPlanExecuteResponse());
  }, [dispatch, selectedVariables]);

  useEffect(() => {
    if (!isLoadingTemplates && !isFetchingTemplates) {
      transformTestCases(editTestCaseId);
      if (editTestCaseId != null) {
        const internalIds: Array<string | undefined> = [];
        selectedScenarios.forEach(scenario => {
          const ids = scenario.testCases.filter(testCase => testCase.id === editTestCaseId).map(testCase => testCase.internalId);
          internalIds.push(...ids);
        });
        dispatch(deleteTestPlanExecuteResponseByIds(internalIds));
        dispatch(setEditTestCaseId(undefined));
      }
    }
  }, [testCaseTemplates, isLoadingTemplates, isFetchingTemplates, dispatch]);

  useEffect(() => {
    dispatch(setShowSpinner(isLoadingTestPlans || isFetchingTestPlans || isLoadingTemplates || isFetchingTemplates || isLoadingScenarios));
  }, [isLoadingTestPlans, isFetchingTestPlans, isLoadingTemplates, isFetchingTemplates, isLoadingScenarios]);

  useEffect(() => {
    if (!!filteredTestPlans.length && selectedTestPlan.id) {
      const findIndex = filteredTestPlans?.findIndex(item => item.id === selectedTestPlan.id);
      if (findIndex > -1) {
        setActiveIndex(findIndex);
        dispatch(setTestPlan({ testPlan: filteredTestPlans[findIndex], scenarios: scenarios || [], templates: testCaseTemplates || [], selectedVariables }));
      } else {
        setActiveIndex(undefined);
        dispatch(deleteTestPlan());
      }
    }
  }, [dispatch, setActiveIndex, filteredTestPlans, testCaseTemplates]);

  useEffect(() => {
    if (!!testPlans) {
      setFilteredTestPlans(testPlans.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase())));
    }
  }, [setFilteredTestPlans, searchValue, testPlans]);

  useEffect(() => {
    dispatch(deleteTestPlanExecuteResponse());
  }, [dispatch, selectedTestPlan]);

  const selectedTestCases: Array<TransformTestCase> = useMemo(() => {
    const result: Array<TransformTestCase> = [];
    selectedScenarios.forEach(scenario => {
      scenario.testCases.forEach(testCase => {
        result.push(testCase);
      });
    });
    return result;
  }, [selectedScenarios]);

  const changeTestPlan = useCallback((dataIndex?: number): void => {
    if (dataIndex != undefined) {
      dispatch(setTestPlan({ testPlan: filteredTestPlans[dataIndex], scenarios: scenarios || [], templates: testCaseTemplates || [], selectedVariables }));
      setActiveIndex(dataIndex);
    } else {
      dispatch(deleteTestPlan());
      dispatch(deleteSelectedTestCaseTemplate());
      setActiveIndex(undefined);
    }
  }, [dispatch, setActiveIndex, filteredTestPlans, scenarios, testCaseTemplates, selectedVariables]);

  const clickRunTestPlan = useCallback(() => {
    dispatch(setTestPlanExecuteResponse(selectedTestCases.map(testCase => ({
      statusCode: 0,
      responseBody: "",
      time: 0,
      internalId: testCase.internalId
    }))));
  }, [dispatch, selectedTestCases])

  const closeTestPlanModal = useCallback((isSave: boolean, createId?: number): void => {
    setShowTestPlanModal(false);
    setEditTestPlan(undefined);
    if (isSave) {
      if (!!createId) {
        const newSelectedTestPlan = {...selectedTestPlan};
        newSelectedTestPlan.id = createId;
        dispatch(setTestPlan({ testPlan: newSelectedTestPlan, scenarios: scenarios || [], templates: testCaseTemplates || [], selectedVariables }));
      }
      refetchTestPlans();
    }
  }, [dispatch, setShowTestPlanModal, setEditTestPlan, refetchTestPlans, selectedTestPlan, scenarios, testCaseTemplates, selectedVariables])

  const clickAddTestPlan = useCallback((): void => {
    setShowTestPlanModal(true);
  }, [setShowTestPlanModal])

  const clickEditTestPlan = useCallback((e: MouseEvent<HTMLDivElement>, testPlan: TestPlan): void => {
    if (selectedTestPlan.id === testPlan.id) {
      e.stopPropagation();
    }
    setEditTestPlan(testPlan);
    setShowTestPlanModal(true);
  }, [setEditTestPlan, setShowTestPlanModal, selectedTestPlan])

  const cancelChangesTestPlan = useCallback((): void => {
    const newSelectedTestPlane = filteredTestPlans.find(item => item.id === selectedTestPlan.id);
    if (newSelectedTestPlane) {
      dispatch(setTestPlan({ testPlan: newSelectedTestPlane, scenarios: scenarios || [], templates: testCaseTemplates || [], selectedVariables }));
    }
  }, [dispatch, filteredTestPlans, selectedTestPlan, scenarios, testCaseTemplates, selectedVariables]);

  const updateExecuteResponse = useCallback((executeResponse: TransformExecuteResponse) => {
    dispatch(updateTestPlanExecuteResponse(executeResponse));
  }, [dispatch])

  const disabledRunButton = useMemo((): boolean => {
    return selectedScenarios.some(item => item.isDeleted || item.isUpdated || !item.internalId) || selectedScenarios.length === 0;
  }, [selectedScenarios]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.mainContainer}>
          <div className={styles.header}>
            <div className={styles.search}>
              <SearchInput value={searchValue} onChange={setSearchValue}/>
            </div>
            <div className={styles.addScenario} onClick={clickAddTestPlan}>
              <AddIcon/>
              {t("testPlan.addNew")}
            </div>
          </div>
          <div className={styles.testPlans}>
            <Accordion activeClassName={styles.active}
                       maxHeight={400}
                       activeIndex={activeIndex}
                       onChangeActive={changeTestPlan}
                       data={!!scenarios
                         ? filteredTestPlans.map(testPlan => {
                           return {
                             title: (
                               <div className={styles.accordionTitle}>
                                 <div className={styles.info}>
                                   <img src={arrowIcon} alt=""
                                        className={styles.arrow}/>
                                   <div className={styles.name}>
                                     <span title={testPlan.name}>{testPlan.name}</span>
                                     {!!testPlan.description && (
                                       <img title={testPlan.description} className={styles.description} src={infoIcon}
                                            alt=""/>
                                     )}
                                   </div>

                                 </div>
                                 <img className={styles.editScenario} src={editIcon}
                                      onClick={(event) => clickEditTestPlan(event, testPlan)} alt=""/>
                               </div>
                             ),
                             child: (
                               <TestPlanScenarios testPlan={testPlan}
                                                  testCaseTemplates={testCaseTemplates}
                                                  refetchTestPlans={refetchTestPlans}
                                                  cancelChanges={cancelChangesTestPlan}/>
                             )
                           }
                         })
                         : []}/>
          </div>
        </div>
        <div className={styles.bottomContainer}>
          <RunTestCasesButton selectedTestCases={selectedTestCases}
                              clickRun={clickRunTestPlan}
                              testCaseTemplates={testCaseTemplates || []}
                              beforeRunIfWasLaunched={transformTestCases}
                              updateExecuteResponse={updateExecuteResponse}
                              disabledRunButton={disabledRunButton}/>
        </div>
      </div>
      {showTestPlanModal && (<TestPlanModal onClose={closeTestPlanModal} testPlan={editTestPlan}/>)}
    </>

  )
}